<template>
  <content-modal
    ref="textToSpeechModal"
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :size="size"
    :auto-hide-off="autoHideOff"
    class="text-to-speech-modal"
    @modal-hide-request="actionHideRequest"
  >
    <template #title>{{ t("tinymce_plugin_text_to_speech_modal_title") }}</template>
    <template #content>
      <modal-content>
        <template #content>
          <cv-form>
            <dl class="field-set type-dropdown">
              <dt class="field-label">{{ t("tinymce_plugin_text_to_speech_language_label") }}</dt>
              <dd class="field-input">
                <dropdown-component
                  v-model="languageSelected"
                  kind="white"
                  class="language-filter"
                  :value="languageSelected"
                  :disabled="textToSpeechGenerated || isGeneratingTextToSpeech"
                  @change="handleChangeLanguageFilter"
                >
                  <cv-dropdown-item
                    v-for="filter in languageFilterList"
                    :key="filter.value"
                    :value="filter.value"
                  >
                    <ellipsis-text :text="t(filter.label)" />
                  </cv-dropdown-item>
                </dropdown-component>
              </dd>
            </dl>
          </cv-form>
          <div v-if="!textToSpeechGenerated || isGeneratingTextToSpeech">
            <cv-form>
              <dl class="field-set type-text description">
                <dt class="field-label">
                  {{ t("tinymce_plugin_text_to_speech_input_text_label") }}
                </dt>
                <dd class="field-input">
                  <text-area
                    ref="textToSpeechDescriptionInput"
                    v-model:value="textToSpeechDescription"
                    :error-message="errorMessage"
                    :character-limit="textToSpeechCharacterLimit"
                    :disabled="isGeneratingTextToSpeech"
                    @input="handleUpdateAudioDescription"
                  />
                </dd>
              </dl>
            </cv-form>
            <cv-button-set>
              <fetch-data-button
                kind="primary"
                size="small"
                :loading="isGeneratingTextToSpeech"
                :disabled="isGeneratingTextToSpeech"
                @click="generateTextToSpeech"
              >
                <span>
                  {{ t("tinymce_plugin_text_to_speech_button_generate") }}
                </span>
              </fetch-data-button>
            </cv-button-set>
          </div>
          <div v-if="textToSpeechGenerated || isPollingMetadata" v-show="!isPollingMetadata">
            <TextToSpeech
              :audio-uuid="audioUuid"
              :audio-text="richTextDescription"
              :in-multi-line="true"
              @media-loaded="handleMediaLoaded"
            />
            <cv-button-set>
              <cv-button class="edit" kind="primary" size="small" @click="handleEditTextToSpeech">
                {{ t("tinymce_plugin_text_to_speech_button_edit") }}
              </cv-button>
            </cv-button-set>
          </div>
        </template>

        <template #tools>
          <cv-button class="cancel" kind="secondary" size="field" @click="actionHideRequest">
            {{ t("tts_button_cancel") }}
          </cv-button>
          <tooltip-button
            class="save"
            kind="primary"
            :disabled="!textToSpeechGenerated"
            :show-tooltip="!textToSpeechGenerated"
            :tip="t('tinymce_plugin_text_to_speech_button_disabled_tooltip')"
            @click="handleSaveTextToSpeech"
          >
            {{ t("button_save") }}
          </tooltip-button>
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, nextTick, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import modalEventBus from "@plugins/modal/service/event-bus";
import randomUtils from "@utils/random.utils";
import {
  LANGUAGE_LIST,
  MS_TA_TTS_CHARACTER_LIMIT,
  ZH_TTS_CHARACTER_LIMIT,
} from "@plugins/richtext/plugins/text-to-speech/utils/text.to.speech.constant";
import { isEmpty } from "@utils/var.check";
import TextToSpeech from "@plugins/richtext/plugins/text-to-speech/components/TextToSpeech.vue";
import mediaObjectApi from "@apis/media.object.api";
import { ApiResponse } from "@apis/api.ts";
import DOMUtils from "@utils/dom.utils";
import TextToSpeechUtils from "@plugins/richtext/plugins/text-to-speech/utils/text.to.speech.utils";
import { NAMESPACE } from "@modules/editor/service/editor.constant";
import { INTEGRATOR_APP_FEATURE_CATEGORY } from "@modules/app/service/app.constant";
import textToSpeechEventBus from "@plugins/richtext/plugins/text-to-speech/service/event-bus";
import EventBusUtils from "@plugins/common/service/event.bus.utils";
import ContentModal from "@plugins/modal/components/ContentModal.vue";
import DropdownComponent from "@plugins/common/components/DropdownComponent.vue";
import ModalContent from "@plugins/modal/components/ModalContent.vue";
import EllipsisText from "@plugins/common/components/EllipsisText.vue";
import TextArea from "@plugins/common/components/TextArea.vue";
import FetchDataButton from "@plugins/common/components/FetchDataButton.vue";
import TooltipButton from "@plugins/common/components/TooltipButton.vue";
import { useDirty } from "@plugins/common/composables/page-dirty.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { MediaObjectModel } from "@plugins/richtext/types.ts";

const props = defineProps<{
  params: {
    context: {
      resourceUuid: string;
      resourceType: string;
      uploadFrom: string;
      resourceDomain: string;
      ownerGroupId: number;
    };
    eventBusWatchInstances?: unknown;
  };
}>();

const { t } = useI18n();
const store = useStore();
const instance = getCurrentInstance();

const id = randomUtils.randomId();
const dirtyId = ref(id);
const dirty = useDirty(dirtyId);
const { passiveApiFailHandler } = useErrorHandler();

const visible = ref(true);
const size = ref("large");
const autoHideOff = ref(true);
const closeAriaLabel = ref("Close");
const isPollingMetadata = ref(false);
const textToSpeechGenerated = ref(false);
const isGeneratingTextToSpeech = ref(false);
const textToSpeech = ref<MediaObjectModel | undefined>(undefined);
const audioUuid = computed(() => textToSpeech.value?.uuid ?? "");
const languageSelected = ref<string>("");
const textToSpeechDescription = ref("");
const error = ref(false);
const errorMessage = ref("");
const richTextDescription = ref("");

const textToSpeechDescriptionInput = ref<InstanceType<typeof TextArea>>();

const textToSpeechCharacterLimit = computed(() => {
  switch (languageSelected.value) {
    case LANGUAGE_LIST.CHINESE.value:
      return ZH_TTS_CHARACTER_LIMIT;
    case LANGUAGE_LIST.MALAY.value:
    case LANGUAGE_LIST.TAMIL.value:
      return MS_TA_TTS_CHARACTER_LIMIT;
    default:
      return ZH_TTS_CHARACTER_LIMIT;
  }
});

const getIntegratorAppAccessByCategory = (category: string) =>
  store.getters[`${NAMESPACE.APP}/getIntegratorAppAccessByCategory`](category);

const languageFilterList = computed(() => {
  const ttsAppList = getIntegratorAppAccessByCategory(INTEGRATOR_APP_FEATURE_CATEGORY.TTS);
  const languageList = Object.values(LANGUAGE_LIST);
  languageList.sort((a, b) => a.value.slice(0, 1).localeCompare(b.value.slice(0, 1))); // sort alphabetically
  return languageList.filter(
    (language) =>
      !isEmpty(
        ttsAppList.find(
          (app: { language: string; accessible: boolean; enabled: boolean }) =>
            app.language === language.encoding && app.accessible && app.enabled,
        ),
      ),
  );
});

onMounted(() => {
  if (languageFilterList.value.length !== 0) {
    languageSelected.value = languageFilterList.value[0].value;
  }
});

function actionHideRequest(event: Record<string, string>) {
  // to prevent close modal upon clicking anywhere outside the modal container
  if (event["cv:reason"] !== "external-click") {
    dirty.promptComponentDirty().then((response) => {
      if (!response) {
        return;
      }
      visible.value = false;
      EventBusUtils.unWatchInstance(props.params.eventBusWatchInstances);
      modalEventBus.$emit("modal-hidden", instance?.vnode.key);
    });
  }
}

function handleEditTextToSpeech() {
  isPollingMetadata.value = false;
  textToSpeechGenerated.value = false;
  isGeneratingTextToSpeech.value = false;
}

function handleSaveTextToSpeech() {
  dirty.removeDirty(id);
  textToSpeechEventBus.$emit("textToSpeechAdded", {
    audioUuid: textToSpeech.value?.uuid,
    audioText: richTextDescription.value,
  });
  visible.value = false;
  EventBusUtils.unWatchInstance(props.params.eventBusWatchInstances);
}

function generateTextToSpeech() {
  isGeneratingTextToSpeech.value = true;
  if (!validate()) {
    error.value = true;
    isGeneratingTextToSpeech.value = false;
    return;
  }

  const language = languageSelected.value;

  // cater for multiline
  const splitText = TextToSpeechUtils.splitTextByDelimiters(
    textToSpeechDescription.value,
    TextToSpeechUtils.getDelimiters(language),
  );

  const paragraph = DOMUtils.createElement("div", {
    class: `paragraph sls-tts-multiline ${language.toLowerCase()}`,
  });

  const description = TextToSpeechUtils.splitTextByNewLine(textToSpeechDescription.value);
  description.forEach((text) => {
    // pre will preserves spaces & line breaks
    // this is used in case there are multiple white spaces in the middle
    const childParagraph = DOMUtils.createElement("pre", { class: "paragraph" });
    // convert only leading whitespace to &nbsp;
    // otherwise the text might break unexpectedly
    const indexOfFirstNonSpace = text.search(/([^\s]+)/);
    childParagraph.innerHTML =
      text.substring(0, indexOfFirstNonSpace).replace(/ /g, "\u00a0") +
      text.substring(indexOfFirstNonSpace);
    paragraph.appendChild(childParagraph);
  });

  const multiLine = splitText.map((text) => {
    return {
      text: text,
    };
  });

  const richTextDescriptionLocal = paragraph.outerHTML;

  richTextDescription.value = richTextDescriptionLocal;

  const textToSpeechData = {
    language: languageSelected.value,
    description: richTextDescriptionLocal,
    multiLine: JSON.stringify(multiLine),
  };

  const editOrGeneratePromise =
    textToSpeech.value !== undefined
      ? updateAudioMO(textToSpeech.value.uuid!, textToSpeechData)
      : generateAudioMO(textToSpeechData);

  editOrGeneratePromise.then(
    (response: ApiResponse<MediaObjectModel>) => {
      if (response.code !== "ok") {
        passiveApiFailHandler();
        return;
      }
      textToSpeech.value = response.data;
      nextTick(() => {
        isPollingMetadata.value = true;
      });
    },
    passiveApiFailHandler as (e: unknown) => void,
  );
  return editOrGeneratePromise;
}

function updateAudioMO(
  audioUuid: string,
  textToSpeechData: { language: string; description: string; multiLine: string },
) {
  return new Promise<ApiResponse<MediaObjectModel>>((resolve, reject) => {
    const requestData = {
      uuid: audioUuid,
      language: TextToSpeechUtils.getEncoding(textToSpeechData.language),
      description: textToSpeechData.description,
      multiLine: textToSpeechData.multiLine,
      resourceDomain: props.params.context.resourceDomain,
      ownerGroupId: props.params.context.ownerGroupId,
    };

    mediaObjectApi.updateAudioFromText(requestData).then(
      //OK
      (response: ApiResponse<MediaObjectModel>) => {
        if (response.code === "error") {
          reject(response);
        }
        resolve(response);
      },
      (response: ApiResponse<MediaObjectModel>) => {
        reject(response);
      },
    );
  });
}

function generateAudioMO(textToSpeechData: {
  language: string;
  description: string;
  multiLine: string;
}) {
  const uuid = randomUtils.randomUuid();

  return new Promise<ApiResponse<MediaObjectModel>>((resolve, reject) => {
    const requestData = {
      uuid: uuid,
      contextUuid: props.params.context.resourceUuid,
      contextType: props.params.context.resourceType,
      uploadFrom: props.params.context.uploadFrom,
      language: TextToSpeechUtils.getEncoding(textToSpeechData.language),
      description: textToSpeechData.description,
      fileName: uuid + ".mp3",
      mimeType: "audio/mpeg",
      title: uuid,
      multiLine: textToSpeechData.multiLine,
    };

    mediaObjectApi.generateAudioFromText(requestData).then(
      //OK
      (response: ApiResponse<MediaObjectModel>) => {
        if (response.code === "error") {
          reject(response);
        }
        resolve(response);
      },
      (response: ApiResponse<MediaObjectModel>) => {
        reject(response);
      },
    );
  });
}

function handleUpdateAudioDescription(event: string) {
  textToSpeechDescription.value = event;
  dirty.setDirty(id);
  clearValidationError();
}

function handleChangeLanguageFilter(data: unknown) {
  if (typeof data === "string") {
    languageSelected.value = data;
    return;
  }
  const maybe = data as { value?: unknown };
  if (maybe && typeof maybe.value === "string") {
    languageSelected.value = maybe.value;
  }
}

function handleMediaLoaded() {
  isGeneratingTextToSpeech.value = false;
  textToSpeechGenerated.value = true;
  isPollingMetadata.value = false;
}

function clearValidationError() {
  error.value = false;
  errorMessage.value = "";
}

function validate() {
  let noError = true;
  if (isEmpty(textToSpeechDescription.value.trim())) {
    errorMessage.value = t("field_empty_error_message");
    noError = false;
  }
  if (!textToSpeechDescriptionInput.value?.validateMaxLimit?.()) {
    errorMessage.value = t(
      "tinymce_plugin_text_to_speech_input_text_character_limit_error_message",
      {
        charLimit: textToSpeechCharacterLimit.value,
      },
    );
    noError = false;
  }
  return noError;
}
//TODO Johann: get permissions based on app access and availability
</script>
